// Quote rotation engine for the dashboard sticky note.
//
// "AI-powered" here = a context-aware recommender: it matches the quote's tone
// to where the student is (streak, due load, mastery), and avoids recently-shown
// quotes via localStorage. Runs fully client-side — zero latency, zero API cost,
// works offline, and never blocks render. (Swappable for a server/LLM source
// later behind the same pickQuoteIndex() contract.)
import { QUOTES, type Quote, type QuoteTone } from '@/content/quotes';

export interface QuoteProgress {
  streak: number;
  dueCount: number;
  greenCount: number;
}

const RECENT_KEY = 'cwy_recent_quotes';
const RECENT_MAX = 24;

function readRecent(): number[] {
  try {
    const v = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    return Array.isArray(v) ? v.filter((x) => typeof x === 'number') : [];
  } catch {
    return [];
  }
}

function pushRecent(idx: number): void {
  const r = readRecent().filter((i) => i !== idx);
  r.push(idx);
  try {
    localStorage.setItem(RECENT_KEY, JSON.stringify(r.slice(-RECENT_MAX)));
  } catch {
    /* storage unavailable — rotation still works, just less de-duped */
  }
}

/** Pick the tone buckets that best fit the student's current state. */
function preferredTones(p: QuoteProgress): QuoteTone[] {
  if (p.streak === 0) return ['start', 'growth', 'consistency'];
  if (p.dueCount >= 12) return ['practice', 'consistency', 'resilience', 'exam'];
  if (p.greenCount >= 5) return ['confidence', 'improvement', 'exam'];
  if (p.streak >= 7) return ['confidence', 'consistency', 'growth'];
  return ['growth', 'improvement', 'exam', 'resilience', 'practice'];
}

/** Choose a quote index: tone-matched, not recently shown. Records the choice. */
export function pickQuoteIndex(p: QuoteProgress): number {
  const tones = preferredTones(p);
  const recent = new Set(readRecent());
  const indexed = QUOTES.map((q, i) => ({ q, i }));
  const toneMatched = indexed.filter(({ q }) => q.tones.some((t) => tones.includes(t)));
  const base = toneMatched.length ? toneMatched : indexed;
  const fresh = base.filter(({ i }) => !recent.has(i));
  const pool = fresh.length ? fresh : base;
  const chosen = pool[Math.floor(Math.random() * pool.length)].i;
  pushRecent(chosen);
  return chosen;
}

export function quoteAt(index: number): Quote {
  return QUOTES[index] ?? QUOTES[0];
}
